import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, ToggleButton, ToggleButtonGroup,
  FormControl, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getAllCommissionProgress, getCommissionProgressByAgentId, getCommissionProgressByGroupName } from '../../api/commissionProgressApi';
import { getPaidBreakdown } from '../../api/commissionSlipApi';
import { getAllAgentGroups } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM, formatCompactRm } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";
const TYPE_COLORS = { PSC: '#1A8CFF', DOC: '#10B981', TOC: '#F59E0B', MOC: '#8B5CF6' };
const STATUS_COLORS = { Completed: '#10B981', Partial: '#F59E0B', Pending: '#94A3B8', Voided: '#EF4444' };
const PIE_COLORS = ['#1A8CFF', '#F59E0B', '#10B981', '#6366F1', '#EF4444', '#8B5CF6'];
const AGENT_TYPES = ['PSC', 'DOC', 'TOC', 'MOC'];

// Match "PSC" / "PSC/2" / "DOC" / "DOC/2" etc.
const baseType = (t) => (t || '').split('/')[0];
const TEAM_TYPES = ['DOC', 'TOC', 'MOC'];

const DATE_PRESETS = [
  { label: 'All Time', start: () => null, end: () => null },
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
];

// All possible tabs. Visibility is computed at render time based on which
// types the current dataset actually contains — Phang Kai (no TOC) won't
// see a TOC tab; managers will see MOC; etc.
const ALL_TABS = [
  { key: 'all',        label: 'All',                       requires: () => true },
  { key: 'individual', label: 'Individual (PSC)',          requires: (types) => types.has('PSC') },
  { key: 'team',       label: 'Team (DOC + TOC + MOC)',    requires: (types) => types.has('DOC') || types.has('TOC') || types.has('MOC') },
  { key: 'PSC',        label: 'PSC',                       requires: (types) => types.has('PSC') },
  { key: 'DOC',        label: 'DOC',                       requires: (types) => types.has('DOC') },
  { key: 'TOC',        label: 'TOC',                       requires: (types) => types.has('TOC') },
  { key: 'MOC',        label: 'MOC',                       requires: (types) => types.has('MOC') },
];

// Customer state is free text and arrives inconsistently — "SELANGOR", "Selangor",
// "PULAU PINANG", "Pulau Penang" and "Pulau Pinang." are all in the data today. Without
// this the agent report lists the same state three times. Trim, drop trailing
// punctuation, Title Case, then fold the known spelling variants. (user 2026-08-26)
const STATE_ALIASES = {
  'Pulau Penang': 'Pulau Pinang',
  'Penang': 'Pulau Pinang',
  'Wp Kuala Lumpur': 'Kuala Lumpur',
  'Kl': 'Kuala Lumpur',
  'N Sembilan': 'Negeri Sembilan',
};
const normState = (raw) => {
  const t = String(raw || '').trim().replace(/[.,;]+$/, '').replace(/\s+/g, ' ');
  if (!t) return '';
  const titled = t.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
  return STATE_ALIASES[titled] || titled;
};

// LEGACY paid — the payment columns on commissionprogress. Kept ONLY as the fallback for
// when the slip feed fails to load, so a network hiccup shows stale figures rather than a
// report claiming nothing has ever been paid. The slip is the real answer; see below.
const legacyPaidOf = (cd) => (cd?.firstPayment || 0) + (cd?.secondPayment || 0)
  + (cd?.repayment || 0) + (cd?.repayment2 || 0) + (cd?.repayment3 || 0) + (cd?.repayment4 || 0) + (cd?.repayment5 || 0);

// A commission row's identity for payment purposes: who gets paid, on which sale, for
// which override type. Slip line items carry exactly this triple, which is what lets the
// slip feed line up against the progress rows.
const rowKeyOf = (r) => {
  const cd = r?.commissionProgressDto, sd = r?.salesDetailsForCommissionProgress;
  if (!cd?.paymentTo || !sd?.salesId) return null;
  return `${cd.paymentTo}|${sd.salesId}|${cd.commPayType || ''}`;
};

const statusFrom = (cd, paid) => {
  if (!cd) return 'Pending';
  if (cd.paymentStatus === 'Voided' || cd.paymentStatus === 'Void') return 'Voided';
  const total = cd.commPay || 0;
  if (total <= 0) return 'Pending';
  if (paid >= total - 0.01) return 'Completed';
  if (paid > 0) return 'Partial';
  return 'Pending';
};

const StatCard = ({ label, value, sub, borderColor }) => (
  <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px' }}>
    <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

// allScope=true renders the company-wide report (Accountant / Super Admin only,
// route-guarded); default is the personal + group report every earner can open.
export default function CommissionReport({ allScope = false }) {
  const { user } = useUser();
  const navigate = useNavigate();

  // Role-driven scope (matches Sales Report):
  //   Sales Agent / In-house Agent → Personal
  //   Leader                        → Personal · Group
  //   Accountant / Super Admin      → Personal · Group · All
  const roles = user?.roles || [];
  const isAccountantOrSuper = roles.includes('Accountant') || roles.includes('Super Admin');
  const hasAny = (list) => roles.some(r => list.includes(r));
  // Split into two PAGES rather than tabs on one (user 2026-08-26):
  //   /reports/commission      → My Commission Report   (Personal, + Group for Leaders)
  //   /reports/commission/all  → All Commission Report  (Accountant / Super Admin ONLY)
  //
  // The company-wide scope is now behind its own route guard, so an agent cannot reach
  // the data at all — previously it was only a hidden tab on a page they could load.
  // `allScope` is set by the route; the role check below is kept as a second gate so the
  // component is safe even if someone wires the route up wrongly.
  const availableScopes = useMemo(() => {
    if (allScope) {
      return isAccountantOrSuper ? [{ key: 'all', label: 'All Commission' }] : [];
    }
    const t = [];
    if (hasAny(['Sales Agent', 'In-house Agent', 'Leader', 'Accountant', 'Super Admin'])) t.push({ key: 'personal', label: 'Personal' });
    if (hasAny(['Leader', 'Accountant', 'Super Admin']))                                  t.push({ key: 'group',    label: 'Group' });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, allScope]);

  const [scopeTab, setScopeTab] = useState(availableScopes[0]?.key || '');
  useEffect(() => {
    if (!scopeTab && availableScopes.length > 0) setScopeTab(availableScopes[0].key);
  }, [availableScopes, scopeTab]);

  // Group picker for Accountant/Super Admin
  const [groups, setGroups] = useState([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  useEffect(() => {
    if (!isAccountantOrSuper) return;
    let cancelled = false;
    getAllAgentGroups().then(r => {
      if (cancelled) return;
      const list = r.data || [];
      setGroups(list);
      if (!selectedGroupName && list.length > 0) setSelectedGroupName(list[0].groupName || list[0].name || '');
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountantOrSuper]);

  const groupNameForQuery = isAccountantOrSuper ? selectedGroupName : (user?.groupName || '');

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to ALL TIME. Opening on "This Month" showed a near-empty report — commission
  // on a deposit made this month is rarely paid yet, so the page looked broken on arrival.
  // (user 2026-08-26)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState('All Time');
  const [activeTab, setActiveTab] = useState('all');

  // The All page must NOT depend on scopeTab. Both routes render the same component at
  // the same position in the tree, so React reuses the instance when you navigate between
  // them and scopeTab stays on whatever the previous page left it — which showed the
  // viewer's own records on a page titled "All Commission Report". Derive the scope from
  // the route instead. (user 2026-08-26)
  const effectiveScope = allScope ? 'all' : scopeTab;

  const fetchData = useCallback(async () => {
    if (!effectiveScope) { setLoading(false); return; }
    setLoading(true);
    try {
      let res;
      if (effectiveScope === 'all') res = await getAllCommissionProgress();
      else if (effectiveScope === 'group') {
        if (!groupNameForQuery) { setRecords([]); setLoading(false); return; }
        res = await getCommissionProgressByGroupName(groupNameForQuery);
      } else {
        res = await getCommissionProgressByAgentId(user?.userId);
      }
      // Active + Dispute Pricing only — Pending is unconfirmed and Archived is out of the
      // book. isArchived is checked separately because some archived sales still carry
      // status "Active". Mirrors commissionAgentDetail and the Sales Report so the three
      // agree. (user 2026-08-26)
      setRecords((res.data || []).filter(r => {
        const sd = r.salesDetailsForCommissionProgress;
        if (!sd) return true;
        if (sd.isArchived) return false;
        return sd.status !== 'Pending' && sd.status !== 'Refunded' && sd.status !== 'Archived';
      }));
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveScope, groupNameForQuery, user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ---------- PAID comes from the slips, not from commissionprogress ----------
     The claim is what Webbey bills Astern; the SLIP is the money that reaches the
     agent. So "Paid" here must be what the agent was actually paid, read out of
     approved slips. (user 2026-08-27: "slips are payment to agent … definitely they
     need to see thru the slip")
     The two records disagree in the field — agent 27 showed RM150,613.91 on the
     progress columns against RM146,206.56 of approved slips — and the slip is the
     one that corresponds to money leaving the account.
     paidLoaded guards the swap: if the feed fails we fall back to the old columns
     rather than render a report saying nobody has ever been paid. */
  const [paidMap, setPaidMap] = useState(() => new Map());
  const [paidLoaded, setPaidLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getPaidBreakdown().then(r => {
      if (cancelled) return;
      const m = new Map();
      (r.data || []).forEach(x => {
        const k = `${x.agentId}|${x.salesId}|${x.type || ''}`;
        m.set(k, (m.get(k) || 0) + (x.payout || 0));
      });
      setPaidMap(m);
      setPaidLoaded(true);
    }).catch(() => { if (!cancelled) setPaidLoaded(false); });
    return () => { cancelled = true; };
  }, []);

  // Paid for a single row. One sale can produce more than one row for the same agent
  // and type, and the slip only knows the triple — so this is the amount paid against
  // the KEY, and callers that sum across rows must use paidForRows() instead.
  const paidOfRow = useCallback((r) => {
    if (!paidLoaded) return legacyPaidOf(r?.commissionProgressDto);
    const k = rowKeyOf(r);
    return k ? (paidMap.get(k) || 0) : 0;
  }, [paidMap, paidLoaded]);

  // Sum paid over a set of rows, de-duplicated by key so a sale that produced several
  // rows for one agent isn't counted once per row.
  const paidForRows = useCallback((rows) => {
    if (!paidLoaded) return rows.reduce((s, r) => s + legacyPaidOf(r.commissionProgressDto), 0);
    const keys = new Set();
    rows.forEach(r => { const k = rowKeyOf(r); if (k) keys.add(k); });
    let sum = 0;
    keys.forEach(k => { sum += paidMap.get(k) || 0; });
    return sum;
  }, [paidMap, paidLoaded]);

  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  /* ---------- Date filter ---------- */
  const dateFiltered = useMemo(() => {
    if (!startDate && !endDate) return records;
    return records.filter(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return false;
      const date = dayjs(dep);
      if (startDate && date.isBefore(startDate, 'day')) return false;
      if (endDate && date.isAfter(endDate, 'day')) return false;
      return true;
    });
  }, [records, startDate, endDate]);

  /* ---------- Agent records (PSC/DOC/TOC/MOC only — Webbey lives in Webbey Report) ---------- */
  const agentRecordsAll = useMemo(() =>
    dateFiltered.filter(r => AGENT_TYPES.includes(baseType(r.commissionProgressDto?.commPayType))),
    [dateFiltered]);

  // Which base types actually appear in this dataset — drives tab visibility.
  const presentTypes = useMemo(() => {
    const s = new Set();
    agentRecordsAll.forEach(r => {
      const b = baseType(r.commissionProgressDto?.commPayType);
      if (b) s.add(b);
    });
    return s;
  }, [agentRecordsAll]);

  const visibleTabs = useMemo(() => ALL_TABS.filter(t => t.requires(presentTypes)), [presentTypes]);

  // If the current activeTab disappeared after a data change (e.g. switched
  // scope from Group → Personal and TOC vanished), fall back to "All".
  useEffect(() => {
    if (!visibleTabs.some(t => t.key === activeTab)) setActiveTab('all');
  }, [visibleTabs, activeTab]);

  /* ---------- Agent commission tab filter ---------- */
  const agentRecords = useMemo(() => {
    return agentRecordsAll.filter(r => {
      const t = baseType(r.commissionProgressDto?.commPayType);
      switch (activeTab) {
        case 'all': return true;
        case 'individual': return t === 'PSC';
        case 'team': return TEAM_TYPES.includes(t);
        case 'PSC':
        case 'DOC':
        case 'TOC':
        case 'MOC': return t === activeTab;
        default: return true;
      }
    });
  }, [agentRecordsAll, activeTab]);

  /* ---------- Agent metrics ---------- */
  const agentTotal = agentRecords.reduce((s, r) => s + (r.commissionProgressDto?.commPay || 0), 0);
  const agentPaid = paidForRows(agentRecords);
  const agentOutstanding = Math.max(0, agentTotal - agentPaid);
  const agentNumRec = agentRecords.length;
  const agentAvg = agentNumRec > 0 ? agentTotal / agentNumRec : 0;
  const agentPaidPct = agentTotal > 0 ? Math.round(agentPaid / agentTotal * 100) : 0;
  const agentPendingCount = agentRecords.filter(r => {
    const s = statusFrom(r.commissionProgressDto, paidOfRow(r));
    return s !== 'Completed' && s !== 'Voided';
  }).length;

  /* ---------- Charts: Agent ---------- */
  const agentByMonth = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return;
      const key = dayjs(dep).format('MMM YYYY');
      map[key] = (map[key] ?? 0) + (r.commissionProgressDto?.commPay || 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [agentRecords]);

  const agentByType = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const t = baseType(r.commissionProgressDto?.commPayType) || 'Unknown';
      map[t] = (map[t] ?? 0) + (r.commissionProgressDto?.commPay || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [agentRecords]);

  const agentByStatus = useMemo(() => {
    const map = { Completed: 0, Partial: 0, Pending: 0, Voided: 0 };
    agentRecords.forEach(r => { const s = statusFrom(r.commissionProgressDto, paidOfRow(r)); map[s] = (map[s] ?? 0) + 1; });
    return Object.entries(map).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [agentRecords, paidOfRow]);

  // EVERY agent, not a top-10 — the accountant needs the whole list to work from.
  // (user 2026-08-26)
  //
  // Revenue is counted on PSC rows ONLY, and deliberately EXCLUDES the add-on (the
  // add-on is its own column). An agent earns DOC/TOC on someone else's sale, so
  // summing sale value across every row would count the same sale under several
  // agents and inflate the total — PSC is the row that marks "this is my sale".
  const topAgents = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const cd = r.commissionProgressDto;
      const sd = r.salesDetailsForCommissionProgress;
      const id = cd?.paymentTo;
      if (!id) return;
      if (!map[id]) map[id] = {
        paymentTo: id, name: cd.paymentToName || 'Unknown',
        total: 0, legacyPaid: 0, count: 0, psc: 0, doc: 0, toc: 0, moc: 0, saleIds: new Set(),
        revenue: 0, addOn: 0, states: new Set(), areas: new Set(), payKeys: new Set(),
      };
      const cp = cd.commPay || 0;
      map[id].total += cp;
      // Paid is collected as (agent, sale, type) KEYS and summed once each at the end —
      // that's the granularity the slip records it at, so adding per row would multiply
      // it when one sale produced several rows for the same agent.
      const pk = rowKeyOf(r);
      if (pk) map[id].payKeys.add(pk);
      map[id].legacyPaid += legacyPaidOf(cd);   // fallback only, if the slip feed is down
      map[id].count += 1;
      // DISTINCT sales this agent earns anything from — not a row count. One sale can
      // produce several rows for the same agent (PSC on their own sale, DOC/TOC on a
      // downline's), so counting rows read as far more deals than were actually closed:
      // KOH JUN HUI had 533 rows across 281 sales. (user 2026-08-26)
      if (sd?.salesId) map[id].saleIds.add(sd.salesId);
      const bt = baseType(cd.commPayType);
      if (bt === 'PSC') map[id].psc += cp;
      else if (bt === 'DOC') map[id].doc += cp;
      else if (bt === 'TOC') map[id].toc += cp;
      else if (bt === 'MOC') map[id].moc += cp;

      // Own sales only — see note above.
      if (bt === 'PSC' && sd) {
        map[id].revenue += sd.finalPrice ?? 0;     // system price, add-on excluded
        map[id].addOn += sd.addOnPrice ?? 0;
        const st = normState(sd.state);
        if (st) map[id].states.add(st);
        // Area = the customer's city/town. Same free-text tidy-up as state, so "KLANG"
        // and "Klang" don't list twice. (user 2026-08-26)
        const ar = normState(sd.city);
        if (ar) map[id].areas.add(ar);
      }
    });
    return Object.values(map)
      .map(a => {
        let paid = a.legacyPaid;
        if (paidLoaded) {
          paid = 0;
          a.payKeys.forEach(k => { paid += paidMap.get(k) || 0; });
        }
        return {
          ...a,
          paid,
          outstanding: Math.max(0, a.total - paid),
          salesInvolved: a.saleIds.size,
          stateList: [...a.states].sort(),
          areaList: [...a.areas].sort(),
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [agentRecords, paidMap, paidLoaded]);


  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  if (loading) return <div>Loading...</div>;

  if (availableScopes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>{allScope ? 'All Commission Report' : 'My Commission Report'}</h2>
        <Typography sx={{ fontFamily: FONT, color: '#64748B', mt: 1 }}>
          Your role does not have access to any Commission Report view.
        </Typography>
      </Box>
    );
  }

  const scopeSubtitle = scopeTab === 'all'
    ? 'PSC + DOC + TOC owed/paid to agents across all groups'
    : scopeTab === 'group'
    ? `Commissions on sales by ${groupNameForQuery || 'your group'}`
    : 'Your personal commissions';

  return (
    <div>
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>

      {/* ===== Header ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>{allScope ? 'All Commission Report' : 'My Commission Report'}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>{scopeSubtitle} · filtered by deposit date</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      {/* Scope tabs (Personal / Group / All) — appear when user has >1 scope */}
      {availableScopes.length > 1 && (
        <Box className="no-print" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive value={scopeTab} onChange={(_, v) => v && setScopeTab(v)} size="small"
            sx={{ '& .MuiToggleButton-root': { fontFamily: FONT, fontSize: '0.78rem', textTransform: 'none', py: 0.6, px: 1.5 },
                  '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
            {availableScopes.map(t => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
          </ToggleButtonGroup>
          {scopeTab === 'group' && isAccountantOrSuper && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={selectedGroupName} onChange={(e) => setSelectedGroupName(e.target.value)}
                displayEmpty sx={{ fontFamily: FONT, fontSize: '0.82rem', borderRadius: '8px' }}>
                {groups.length === 0 && <MenuItem value="" disabled>No groups</MenuItem>}
                {groups.map(g => {
                  const name = g.groupName || g.name || '';
                  return <MenuItem key={name} value={name} sx={{ fontFamily: FONT, fontSize: '0.82rem' }}>{name}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {/* Date presets */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap', borderBottom: '1px solid #E2E8F0', pb: 1 }}>
        {visibleTabs.map(t => (
          <Chip key={t.key} label={t.label} size="small" onClick={() => setActiveTab(t.key)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activeTab === t.key ? 700 : 500,
              backgroundColor: activeTab === t.key ? '#0F172A' : '#FFFFFF',
              color: activeTab === t.key ? '#fff' : '#475569',
              border: '1px solid',
              borderColor: activeTab === t.key ? '#0F172A' : '#E2E8F0',
              '&:hover': { backgroundColor: activeTab === t.key ? '#1E293B' : '#F8FAFC' },
            }} />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <StatCard label="Total Commission" value={handlePriceWithRM(agentTotal)} sub={`${agentNumRec} records`} borderColor="#1A8CFF" />
        <StatCard label="Paid" value={handlePriceWithRM(agentPaid)} sub={`${agentPaidPct}% · from approved slips`} borderColor="#10B981" />
        <StatCard label="Outstanding" value={handlePriceWithRM(agentOutstanding)} sub={`${agentPendingCount} pending`} borderColor="#F59E0B" />
        <StatCard label="Average / Record" value={handlePriceWithRM(Math.round(agentAvg))} sub="Per commission" borderColor="#6366F1" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Commission by Month</Typography>
            {agentByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={agentByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis tickFormatter={v => formatCompactRm(v)} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip formatter={value => handlePriceWithRM(value)} />
                  <Bar dataKey="value" fill="#1A8CFF" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Commission by Type</Typography>
            {agentByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={agentByType} cx="50%" cy="50%" innerRadius={60} outerRadius={85} dataKey="value"
                    label={({ name, value }) => `${name}: ${handlePriceWithRM(value)}`} labelLine={false}>
                    {agentByType.map((entry, i) => (
                      <Cell key={entry.name} fill={TYPE_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={value => handlePriceWithRM(value)} />
                  <Legend formatter={value => <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Payment Status</Typography>
            {agentByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={agentByStatus} cx="50%" cy="50%" innerRadius={60} outerRadius={85} dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {agentByStatus.map((entry, i) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={value => <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>Quick Stats</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>Snapshot for selected period & tab</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {AGENT_TYPES.filter(t => presentTypes.has(t)).map(t => {
                const recs = agentRecordsAll.filter(r => baseType(r.commissionProgressDto?.commPayType) === t);
                const sum = recs.reduce((s, r) => s + (r.commissionProgressDto?.commPay || 0), 0);
                return (
                  <Box key={t} sx={{ p: 1.5, borderRadius: '8px', backgroundColor: '#F8FAFC', borderLeft: `3px solid ${TYPE_COLORS[t]}` }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{t}</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>{handlePriceWithRM(sum)}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: FONT }}>{recs.length} records</Typography>
                  </Box>
                );
              })}
              {presentTypes.size === 0 && (
                <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: FONT, gridColumn: '1 / -1', textAlign: 'center', py: 2 }}>
                  No commission types received in the selected period.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>


      {/* Per-agent breakdown — ALL COMMISSION REPORT ONLY.
          On "My Commission Report" this listed every teammate's commission, revenue and
          add-on on a page whose whole point is the viewer's own earnings — a Leader on
          the Group tab could read the whole team name by name. Gated to allScope: group
          totals and charts stay, the name-by-name table does not. (user 2026-08-26) */}
      {allScope && (
      <Card sx={{ borderRadius: '14px', mb: 4 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>All Agents</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>{topAgents.length} agent{topAgents.length === 1 ? '' : 's'} · sorted by total commission · click a row for the full breakdown</Typography>
          {topAgents.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell align="right">Sales Involved</TableCell>
                    <TableCell align="right">PSC</TableCell>
                    <TableCell align="right">DOC</TableCell>
                    <TableCell align="right">TOC</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Add-on</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAgents.map((a, i) => (
                    <TableRow key={a.paymentTo} hover
                      onClick={() => navigate(`/reports/commission/agent/${a.paymentTo}`)}
                      sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: '#1A8CFF' }}>{a.name}</TableCell>
                      <TableCell align="right">{a.salesInvolved}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.psc)}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.doc)}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.toc)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{handlePriceWithRM(a.total)}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981' }}>{handlePriceWithRM(a.paid)}</TableCell>
                      <TableCell align="right" sx={{ color: '#F59E0B' }}>{handlePriceWithRM(a.outstanding)}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.revenue)}</TableCell>
                      <TableCell align="right" sx={{ color: '#8B5CF6' }}>{handlePriceWithRM(a.addOn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data for selected period</Typography>}
        </CardContent>
      </Card>
      )}

    </div>
  );
}
